/**
 * The wire format of `users/{uid}/settings/main`.
 *
 * Pure functions only — no DOM, no Firebase. `features/account/index.js` does
 * the reading and writing; this file only knows the shape.
 *
 * ── Why the shape is not negotiable ───────────────────────────────────────
 * Three programs read this document and none of them can be changed
 * independently:
 *
 *   1. the legacy app (`app.html`, `pushSettingsToCloud` / `hydrateSettings-
 *      FromCloud`) — still the only place most settings are edited;
 *   2. the backend (`functions/alerts/notify-prefs.js`, `alert-schedule.js`)
 *      — decides which emails to send;
 *   3. this, the ported Account screen.
 *
 * Every value is wrapped: `{ v: '<the value, as a STRING>', at: '<iso>' }`.
 * `v` is a string even when the value is an object, because the legacy writer
 * stores whatever `localStorage` held, and `localStorage` holds strings. A
 * bare object written here would be read back by the legacy app as an object
 * where it expects JSON text, and it would silently keep the old value.
 *
 * `at` is how the two writers agree. Last write wins, compared as ISO strings
 * — which sorts correctly precisely because they are ISO. Do not switch to
 * epoch numbers on one side only.
 *
 * ── Absent means ON ───────────────────────────────────────────────────────
 * Almost every document in production is missing most of these keys. If
 * missing meant "off", reading this file would silently stop every email the
 * product sends. `parseToggles` therefore only accepts an explicit `false`.
 * The backend follows the identical rule; the two must not drift.
 */

/** Notification switches. The key the legacy app and the backend both use. */
export const TOGGLES_KEY = 'mbm_acct_toggles'

/** Daily action email schedule. Same document, same wrapper. */
export const ALERT_PREFS_KEY = 'mbm_alert_prefs'

/**
 * The three switches that control real behaviour, and their defaults.
 *
 * Four others shipped and were decoration — `2fa`, `notifBooking`,
 * `notifPush`, `notifSound`. They were removed on 14 Aug (HANDOFF §5o)
 * because nothing implemented them. Do not add a switch here without the code
 * that obeys it; a switch that does not switch anything is a promise.
 */
export const DEFAULT_TOGGLES = Object.freeze({
  notifTrips: true, // "Departing within 7 days" in the daily action email
  notifPayments: true, // "Final payments due within 3 days", same email
  notifWeekly: true, // the Monday digest
})

/** Keys that were switches once and must be actively cleaned up. */
export const DEAD_TOGGLE_KEYS = Object.freeze(['2fa', 'notifBooking', 'notifPush', 'notifSound'])

/** @typedef {{notifTrips: boolean, notifPayments: boolean, notifWeekly: boolean}} NotifyToggles */
/** @typedef {{enabled: boolean, hour: number, minute: number, tz: string}} AlertPrefs */

/**
 * Unwrap one entry from the settings document.
 *
 * Accepts both shapes on purpose: `{v, at}` is what the legacy writer
 * produces and is what will actually be there, but a value written by hand in
 * the console — or by any future path that forgets the wrapper — must not
 * read as "no preference" and quietly re-enable an email somebody turned off.
 *
 * @param {Record<string, any>|null|undefined} settings
 * @param {string} key
 * @returns {any} the inner value, or undefined
 */
export function unwrap(settings, key) {
  const entry = settings && settings[key]
  if (entry == null) return undefined
  if (typeof entry === 'object' && !Array.isArray(entry) && 'v' in entry) return entry.v
  return entry
}

/**
 * Wrap a value for writing. `at` is supplied rather than read from the clock
 * so that a caller can stamp several keys with one timestamp, and so tests do
 * not depend on the time of day.
 *
 * @param {any} value - serialised to JSON text when it is not already a string
 * @param {string} atIso
 * @returns {{v: string, at: string}}
 */
export function wrap(value, atIso) {
  const v = typeof value === 'string' ? value : JSON.stringify(value)
  return { v, at: atIso }
}

/**
 * Which side is newer, for one key. Returns true when the CLOUD copy should
 * win. Ties go to local: a value the advisor just typed is not overwritten by
 * a value written in the same second somewhere else.
 *
 * @param {string} cloudAtIso
 * @param {string} localAtIso
 * @returns {boolean}
 */
export function cloudWins(cloudAtIso, localAtIso) {
  const cloud = String(cloudAtIso || '')
  const local = String(localAtIso || '')
  if (!cloud) return false
  if (!local) return true
  return cloud > local
}

/**
 * Read the notification switches. Mirrors `functions/alerts/notify-prefs.js`
 * exactly — if you change one, change the other in the same commit.
 *
 * @param {Record<string, any>|null|undefined} settings
 * @returns {NotifyToggles}
 */
export function parseToggles(settings) {
  const raw = unwrap(settings, TOGGLES_KEY)

  /** @type {any} */
  let parsed = null
  if (typeof raw === 'string' && raw) {
    try {
      parsed = JSON.parse(raw)
    } catch {
      parsed = null
    }
  } else if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    parsed = raw
  }

  const out = { ...DEFAULT_TOGGLES }
  if (!parsed || typeof parsed !== 'object') return out

  for (const key of Object.keys(DEFAULT_TOGGLES)) {
    /* ONLY an explicit false turns anything off. undefined, null, 'false',
       0 — all of it means "not switched off". The cost of being wrong in
       this direction is one email too many; the cost the other way is an
       advisor missing a final payment. */
    if (parsed[key] === false) out[key] = false
  }
  return out
}

/**
 * Serialise the switches back, dropping the four dead keys so a stale value
 * cannot outlive the switch that wrote it.
 *
 * @param {Record<string, any>|null|undefined} toggles
 * @returns {NotifyToggles}
 */
export function serializeToggles(toggles) {
  const out = { ...DEFAULT_TOGGLES }
  for (const key of Object.keys(DEFAULT_TOGGLES)) {
    if (toggles && toggles[key] === false) out[key] = false
  }
  return out
}

/**
 * The daily action email's schedule.
 *
 * A missing schedule means "8:00 AM in this device's zone, enabled" — the
 * same default the backend applies. §5e was this exact bug in the other
 * direction: the screen showed "off" while the server, seeing nothing stored,
 * sent anyway. What is on screen must be what is stored.
 *
 * @param {Record<string, any>|null|undefined} settings
 * @param {string} [deviceTz]
 * @returns {AlertPrefs}
 */
export function parseAlertPrefs(settings, deviceTz = 'America/Chicago') {
  const raw = unwrap(settings, ALERT_PREFS_KEY)

  /** @type {any} */
  let parsed = null
  if (typeof raw === 'string' && raw) {
    try {
      parsed = JSON.parse(raw)
    } catch {
      parsed = null
    }
  } else if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    parsed = raw
  }

  const p = parsed && typeof parsed === 'object' ? parsed : {}
  const hour = Number(p.hour)
  return {
    enabled: p.enabled !== false,
    hour: Number.isFinite(hour) && hour >= 0 && hour <= 23 ? Math.floor(hour) : 8,
    minute: Number(p.minute) === 30 ? 30 : 0,
    tz: typeof p.tz === 'string' && p.tz ? p.tz : deviceTz,
  }
}

/**
 * @param {Record<string, any>|null|undefined} prefs
 * @param {string} [deviceTz]
 * @returns {AlertPrefs}
 */
export function serializeAlertPrefs(prefs, deviceTz = 'America/Chicago') {
  return parseAlertPrefs({ [ALERT_PREFS_KEY]: prefs }, deviceTz)
}

/**
 * "8:00 AM" — for the hint under the schedule.
 * @param {{hour?: number, minute?: number}|null|undefined} prefs
 * @returns {string}
 */
export function prettyTime(prefs) {
  const p = prefs || { hour: 8, minute: 0 }
  const h24 = Number(p.hour) || 0
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12
  const mm = Number(p.minute) === 30 ? '30' : '00'
  return `${h12}:${mm} ${h24 < 12 ? 'AM' : 'PM'}`
}

/**
 * This device's IANA time zone, or a stated fallback when the browser will
 * not say. Isolated here so the model stays pure and testable.
 * @param {string} [fallback]
 * @returns {string}
 */
export function deviceTimeZone(fallback = 'America/Chicago') {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || fallback
  } catch {
    return fallback
  }
}
